import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Drawer,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import {
  PageHeader,
  CardContainer,
  PrimaryButton,
  SecondaryButton,
  EmptyState,
  LoadingSkeleton,
  StatusChip,
} from '../../components/ui';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  aggregatorApi,
  AdjustmentKind,
  AggregatorSettlement,
  SettlementAdjustment,
} from '../../api/aggregatorApi';
import SettlementEditDrawer from '../../components/finance/SettlementEditDrawer';

const KINDS: AdjustmentKind[] = ['REFUND', 'CHARGEBACK', 'PROMO', 'TIP_CORRECTION', 'OTHER'];

const SettlementDetailPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const settlementId = Number(id);

  const [settlement, setSettlement] = useState<AggregatorSettlement | null>(null);
  const [adjustments, setAdjustments] = useState<SettlementAdjustment[]>([]);
  const [loading, setLoading] = useState(true);

  // Adjustment drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerKind, setDrawerKind] = useState<AdjustmentKind>('REFUND');
  const [drawerAmount, setDrawerAmount] = useState('');
  const [drawerDate, setDrawerDate] = useState(new Date().toISOString().slice(0, 10));
  const [drawerNote, setDrawerNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteSettlement, setConfirmDeleteSettlement] = useState(false);
  const [deletingSettlement, setDeletingSettlement] = useState(false);

  // Edit settlement drawer — extracted into <SettlementEditDrawer> for reuse
  // from EOD recovery flow.
  const [editOpen, setEditOpen] = useState(false);
  const openEditDrawer = () => {
    if (!settlement) return;
    setEditOpen(true);
  };

  const reload = useCallback(async () => {
    if (!Number.isFinite(settlementId) || settlementId <= 0) return;
    setLoading(true);
    try {
      const [sRes, aRes] = await Promise.all([
        aggregatorApi.getSettlement(settlementId),
        aggregatorApi.listAdjustments(settlementId),
      ]);
      setSettlement(sRes.data);
      setAdjustments(aRes.data);
    } catch {
      toast.error('Failed to load settlement');
    } finally {
      setLoading(false);
    }
  }, [settlementId]);

  useEffect(() => { reload(); }, [reload]);

  const submitAdjustment = async () => {
    const amt = parseFloat(drawerAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setSubmitting(true);
    try {
      await aggregatorApi.addAdjustment(settlementId, {
        adjustmentDate: drawerDate,
        amount: amt,
        kind: drawerKind,
        note: drawerNote.trim() || undefined,
      });
      toast.success('Adjustment recorded');
      setDrawerOpen(false);
      setDrawerAmount('');
      setDrawerNote('');
      setDrawerKind('REFUND');
      await reload();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e?.response?.data?.message || 'Failed to record adjustment');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (pendingDelete == null) return;
    setDeleting(true);
    try {
      await aggregatorApi.deleteAdjustment(settlementId, pendingDelete);
      toast.success('Adjustment removed');
      setPendingDelete(null);
      await reload();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSettlement = async () => {
    if (!settlement) return;
    setDeletingSettlement(true);
    try {
      await aggregatorApi.deleteSettlement(settlement.id);
      toast.success('Settlement deleted. Entries returned to pending.');
      navigate('/vendor/finance/aggregators');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e?.response?.data?.message || 'Failed to delete settlement');
    } finally {
      setDeletingSettlement(false);
      setConfirmDeleteSettlement(false);
    }
  };

  if (loading || !settlement) {
    return (
      <Box sx={{ p: 3, backgroundColor: theme.background, minHeight: '100vh' }}>
        <LoadingSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton size="small" onClick={() => navigate('/vendor/finance/aggregators')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <PageHeader
            title={`${settlement.channelName} · ${settlement.paymentDate}`}
            subtitle={
              settlement.periodFrom
                ? `Covers ${settlement.periodFrom}${settlement.periodTo && settlement.periodTo !== settlement.periodFrom ? ` → ${settlement.periodTo}` : ''} · Settlement audit, allocations, and adjustment journal`
                : 'Settlement audit, allocations, and adjustment journal'
            }
          />
        </Box>
        <IconButton size="small" onClick={openEditDrawer} title="Edit settlement">
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setConfirmDeleteSettlement(true)}
          title="Delete settlement"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* KPI strip */}
      <CardContainer sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 600 }}>Reconciliation summary</Typography>
          <StatusChip status={settlement.status} variant="info" />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5 }}>
          <Stat label="Reported gross" value={formatCurrency(settlement.grossReported)} />
          <Stat label="Bank paid" value={formatCurrency(settlement.paymentAmount)} />
          <Stat label="Commission expected" value={formatCurrency(settlement.commissionExpected)} />
          <Stat label="Commission actual" value={formatCurrency(settlement.commissionActual)} />
          <Stat label="Adjustments" value={formatCurrency(settlement.adjustmentAmount)} />
          <Stat
            label="Unexplained"
            value={formatCurrency(settlement.unexplainedVariance)}
            highlight={Math.abs(settlement.unexplainedVariance) >= 0.01}
          />
        </Box>
        {(settlement.periodFrom || settlement.periodTo) && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 1.5, pt: 1.5, borderTop: `1px solid ${theme.gray?.[200] || '#E8E5E0'}` }}>
            <Stat label="Payment date" value={settlement.paymentDate} />
            <Stat label="Period from" value={settlement.periodFrom || '—'} />
            <Stat label="Period to" value={settlement.periodTo || '—'} />
          </Box>
        )}
      </CardContainer>

      {/* Allocations */}
      <CardContainer sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
          Allocations ({settlement.allocations.length})
        </Typography>
        {settlement.allocations.length === 0 ? (
          <EmptyState title="No entries allocated" />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {settlement.allocations.map((a, i) => (
              <Box
                key={`${a.entryType}-${a.entryId}-${i}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  px: 1,
                  borderBottom: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography variant="caption" sx={{ color: theme.gray?.[700] || '#444' }}>
                  {a.entryType} #{a.entryId}{a.entryDate ? ` · ${a.entryDate}` : ''}
                  {a.sourceLabel ? ` · ${a.sourceLabel}` : ''}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {formatCurrency(a.allocatedAmount)}
                  {a.entryGrossAmount != null && a.entryGrossAmount !== a.allocatedAmount && (
                    <Typography component="span" variant="caption" sx={{ color: theme.gray?.[500] || '#888', ml: 0.5 }}>
                      / {formatCurrency(a.entryGrossAmount)}
                    </Typography>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContainer>

      {/* Adjustment journal */}
      <CardContainer sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 600 }}>Adjustment journal ({adjustments.length})</Typography>
          <PrimaryButton onClick={() => setDrawerOpen(true)}>Add adjustment</PrimaryButton>
        </Box>
        {adjustments.length === 0 ? (
          <EmptyState
            title="No adjustments yet"
            subtitle="Record refunds, chargebacks, promo top-ups, or tip corrections that change the unexplained variance."
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {adjustments.map((a) => (
              <Box
                key={a.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '110px 110px 1fr auto auto',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  borderBottom: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography variant="caption" sx={{ color: theme.gray?.[600] || '#666' }}>
                  {a.adjustmentDate}
                </Typography>
                <KindChip kind={a.kind} />
                <Typography variant="caption" sx={{ color: theme.text }}>
                  {a.note || '—'}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {formatCurrency(a.amount)}
                </Typography>
                <IconButton size="small" onClick={() => setPendingDelete(a.id)}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </CardContainer>

      {/* Add adjustment drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => !submitting && setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, p: 2 } }}
      >
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 2 }}>Add adjustment</Typography>
        <Stack spacing={2}>
          <TextField
            select
            label="Kind"
            value={drawerKind}
            onChange={(e) => setDrawerKind(e.target.value as AdjustmentKind)}
          >
            {KINDS.map((k) => (
              <MenuItem key={k} value={k}>{prettyKind(k)}</MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label="Adjustment date"
            value={drawerDate}
            onChange={(e) => setDrawerDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="number"
            label="Amount"
            value={drawerAmount}
            onChange={(e) => setDrawerAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />
          <TextField
            label="Note (optional)"
            value={drawerNote}
            onChange={(e) => setDrawerNote(e.target.value)}
            multiline
            minRows={2}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <SecondaryButton onClick={() => setDrawerOpen(false)} disabled={submitting}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={submitAdjustment} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </PrimaryButton>
          </Box>
        </Stack>
      </Drawer>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete adjustment?"
        message="The settlement will be re-computed without this entry."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <SettlementEditDrawer
        open={editOpen}
        settlementId={Number.isFinite(settlementId) && settlementId > 0 ? settlementId : null}
        onClose={() => setEditOpen(false)}
        onSaved={() => { reload(); }}
      />

      <ConfirmDialog
        open={confirmDeleteSettlement}
        title="Delete this settlement?"
        message={`Removing this settlement will return ${settlement.allocations.length} allocated entr${settlement.allocations.length === 1 ? 'y' : 'ies'} to pending. Any auto-derived income or commission expense rows tied to it will also be removed. This cannot be undone.`}
        confirmLabel="Delete settlement"
        cancelLabel="Cancel"
        variant="danger"
        loading={deletingSettlement}
        onConfirm={handleDeleteSettlement}
        onCancel={() => setConfirmDeleteSettlement(false)}
      />
    </Box>
  );
};

const Stat: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => {
  const { theme } = useTheme();
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: theme.gray?.[500] || '#888', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.04em' }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontWeight: 700, fontSize: '0.95rem', color: highlight ? (theme.warning || '#D97706') : theme.text, whiteSpace: 'nowrap' }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const KIND_COLORS: Record<AdjustmentKind, { bg: string; fg: string }> = {
  REFUND: { bg: '#FFE4E1', fg: '#9A2E27' },
  CHARGEBACK: { bg: '#FEE2C7', fg: '#C2410C' },
  PROMO: { bg: '#DCFCE7', fg: '#10603C' },
  TIP_CORRECTION: { bg: '#E0F2FE', fg: '#075985' },
  OTHER: { bg: '#F1F5F9', fg: '#475569' },
};

const KindChip: React.FC<{ kind: AdjustmentKind }> = ({ kind }) => {
  const c = KIND_COLORS[kind] || KIND_COLORS.OTHER;
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.25,
        borderRadius: '6px',
        backgroundColor: c.bg,
        color: c.fg,
        fontSize: '0.65rem',
        fontWeight: 700,
        textAlign: 'center',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        width: 'fit-content',
      }}
    >
      {prettyKind(kind)}
    </Box>
  );
};

function prettyKind(k: AdjustmentKind): string {
  if (k === 'TIP_CORRECTION') return 'Tip Correction';
  return k.charAt(0) + k.slice(1).toLowerCase();
}

export default SettlementDetailPage;
